import type { WorkflowNodeType } from "@/types/aiToolkit";

/**
 * Canvas metadata for every engine node type (src/types/aiToolkit.ts). Colors
 * are hsl tokens so they read in light + dark. `executes` mirrors the engine:
 * `loop` and `http-request` are declared but currently no-op in
 * workflowEngineService — flagged so the canvas can warn (Phase 4A.3/4A.4).
 */
export interface NodeMeta {
  type: WorkflowNodeType;
  label: string;
  color: string;
  group: "flow" | "agent" | "data" | "control";
  executes: boolean;
  /** which data.* fields the config panel should surface (already executed by the engine) */
  fields: Array<
    | "label"
    | "description"
    | "agentId"
    | "promptTemplate"
    | "toolId"
    | "toolOperation"
    | "condition"
    | "conditionValue"
    | "confidenceThreshold"
    | "qualityThreshold"
    | "mergeStrategy"
    | "knowledgeSource"
    | "skillIds"
    | "workerProfileId"
    | "loopCount"
    | "delayMs"
    | "httpConfig"
    | "crewMaxRounds"
  >;
}

export const NODE_META: Record<WorkflowNodeType, NodeMeta> = {
  trigger: { type: "trigger", label: "Trigger", color: "#64748b", group: "flow", executes: true, fields: ["label", "description"] },
  agent: { type: "agent", label: "Agent", color: "#7c3aed", group: "agent", executes: true, fields: ["label", "agentId", "workerProfileId", "skillIds", "description"] },
  crew: { type: "crew", label: "Crew (manager)", color: "#9333ea", group: "agent", executes: true, fields: ["label", "agentId", "promptTemplate", "crewMaxRounds", "description"] },
  tool: { type: "tool", label: "Tool", color: "#0891b2", group: "agent", executes: true, fields: ["label", "toolId", "toolOperation", "description"] },
  prompt: { type: "prompt", label: "Prompt", color: "#2563eb", group: "agent", executes: true, fields: ["label", "promptTemplate"] },
  knowledge: { type: "knowledge", label: "Knowledge", color: "#0d9488", group: "data", executes: true, fields: ["label", "knowledgeSource", "description"] },
  transform: { type: "transform", label: "Transform", color: "#ca8a04", group: "data", executes: true, fields: ["label", "description"] },
  validation: { type: "validation", label: "Validation", color: "#16a34a", group: "control", executes: true, fields: ["label", "description"] },
  "confidence-gate": { type: "confidence-gate", label: "Confidence Gate", color: "#d97706", group: "control", executes: true, fields: ["label", "confidenceThreshold"] },
  "quality-gate": { type: "quality-gate", label: "Quality Gate", color: "#dc2626", group: "control", executes: true, fields: ["label", "qualityThreshold"] },
  condition: { type: "condition", label: "Condition", color: "#9333ea", group: "control", executes: true, fields: ["label", "condition", "conditionValue"] },
  "entity-resolution": { type: "entity-resolution", label: "Entity Resolution", color: "#0284c7", group: "data", executes: true, fields: ["label", "description"] },
  merge: { type: "merge", label: "Merge", color: "#059669", group: "flow", executes: true, fields: ["label", "mergeStrategy"] },
  parallel: { type: "parallel", label: "Parallel", color: "#7c3aed", group: "flow", executes: true, fields: ["label", "description"] },
  "human-review": { type: "human-review", label: "Human Review", color: "#e11d48", group: "control", executes: true, fields: ["label", "description"] },
  delay: { type: "delay", label: "Delay", color: "#6b7280", group: "flow", executes: true, fields: ["label", "delayMs"] },
  output: { type: "output", label: "Output", color: "#475569", group: "flow", executes: true, fields: ["label", "description"] },
  // 4A.4: bounded loop — "loop" branch runs the body loopCount times, then "done".
  loop: { type: "loop", label: "Loop", color: "#a16207", group: "control", executes: true, fields: ["label", "loopCount"] },
  // 4A.3: https-only, private hosts blocked, 30s timeout; {{path}} interpolation.
  "http-request": { type: "http-request", label: "HTTP Request", color: "#0369a1", group: "data", executes: true, fields: ["label", "httpConfig"] },
};

export const PALETTE_ORDER: WorkflowNodeType[] = [
  "trigger", "agent", "crew", "tool", "prompt", "knowledge",
  "condition", "confidence-gate", "quality-gate", "parallel", "merge",
  "transform", "entity-resolution", "human-review", "delay", "output",
  "loop", "http-request",
];
